'use client';

import { useFormState } from 'react-dom';
import { ReactNode, useCallback, useEffect, useRef } from 'react';
import {
	ContactFormServerResponse,
	submitContactForm,
} from './ContactForm.server';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { hidden } from '@/uiPrimitives/utility/display.css';
import { useGoogleReCaptcha } from '@dop/shared/components/GoogleReCaptcha/GoogleReCaptchaProvider';
import { box } from '@/uiPrimitives/layout/box';
import { stack } from '@/uiPrimitives/layout/stack';
import { h3CSS } from '@/uiComposites/typography/headings';
import { ContactFormWebApiData } from './ContactForm.types';
import { textBlock } from '@/uiPrimitives/typography/text';
import { useContactForm } from './useContactForm';
import { SubmitMessage } from './SubmitMessage';
import { translateInline } from '@/globals/translate/translateInline';
import { useDOPRouter } from '@/globals/routing/DOPRouter';
import { tryAsync } from '@/globals/helpers/try';

export const ContactFormClient = ({
	form,
	fieldsSlot,
	previewSlot,
}: {
	form: ContactFormWebApiData['form'];
	fieldsSlot: ReactNode;
	previewSlot: ReactNode;
}) => {
	const { startTransition } = useDOPRouter();
	const [contactFormState] = useContactForm();
	const executeRecaptcha = useGoogleReCaptcha();
	const [, dispatch] = useContactForm();
	const ref = useRef<HTMLFormElement>(null);

	const rejectFormResponse = useCallback(() => {
		dispatch({ type: 'showServerError' });
		return 'rejected';
	}, [dispatch]);

	const [formResponse, formAction] = useFormState(
		async (state: ContactFormServerResponse, formData: FormData) => {
			if (state === 'fulfilled') {
				return state;
			}

			if (executeRecaptcha == null) {
				// Unable to get recaptcha-function
				return rejectFormResponse();
			}

			const recaptchaTry = await tryAsync(async () => await executeRecaptcha());
			if (!recaptchaTry.success) {
				return rejectFormResponse();
			}

			formData.set('recaptchaResponse', recaptchaTry.result || '');

			const apiPathname = `/${form.action.join('/')}/`;
			// Don't destructure handleSubmit; ts/vscode don't understand it is a valid import of a server action
			const submitTry = await tryAsync(
				async () => await submitContactForm(apiPathname, formData),
			);
			if (!submitTry.success) {
				return rejectFormResponse();
			}

			if (submitTry.result === 'fulfilled') {
				dispatch({ type: 'resetContactForm' });
				return submitTry.result;
			}

			return rejectFormResponse();
		},
		'idle',
	);

	const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const formData = new FormData(event.currentTarget);
		startTransition(() => {
			formAction(formData);
		});
	};

	useEffect(() => {
		if (contactFormState?.isDirty)
			// Do a smooth scroll when user is directed to errors
			ref.current?.scrollIntoView({
				behavior: 'auto',
			});
	}, [contactFormState?.isDirty, contactFormState?.previewMode]);

	useEffect(() => {
		// During the server-action, there was an error: scroll to the summary at the top
		if (formResponse === 'rejected') {
			ref.current?.scrollIntoView({ behavior: 'smooth' });
		}
	}, [formResponse]);

	return (
		<form ref={ref} onSubmit={onSubmit}>
			{formResponse === 'fulfilled' ? (
				<SubmitMessage />
			) : (
				<div className={classes(stack({}))}>
					<div role="alert">
						{contactFormState.showServerError && (
							<div
								className={classes(
									box({
										borderColor: 'positiveRed',
										borderWidth: '4px',
										borderInlineStartStyle: 'solid',
										backgroundColor: 'negativeRed',
										padding: '4 | betweenListItems',
									}),
								)}
							>
								<div
									className={classes(
										stack({ rowGap: '5 | betweenParagraphs' }),
									)}
								>
									<h2 className={classes(h3CSS)}>
										{translateInline({
											en: 'An error occurred',
											nl: 'Er is iets misgegaan',
										})}
									</h2>
									<p className={classes(textBlock({}))}>
										{translateInline({
											en: 'Due to a technical problem your data has not been sent. Please try again later.',
											nl: 'Het versturen van uw gegevens is niet gelukt door een technisch probleem. Probeer het later nogmaals.',
										})}
									</p>
								</div>
							</div>
						)}
					</div>
					<div
						className={classes(contactFormState?.previewMode ? hidden : null)}
					>
						{fieldsSlot}
					</div>
					<div
						className={classes(!contactFormState?.previewMode ? hidden : null)}
					>
						{previewSlot}
					</div>
				</div>
			)}
		</form>
	);
};
